import type { EntityInstance, ParsedLevel, SimState } from '@md/engine';
import { MAGNET_RANGE_TILES } from '@md/shared';
import { Application, Container, Graphics } from 'pixi.js';
import { computeCamera } from './camera';
import { createParticleField, type ParticleField } from './particles';
import { DEFAULT_THEME, themeForWorld, type WorldTheme } from './themes';

/**
 * Rendu PixiJS v8 (spec §1).
 *
 * Le renderer est un pur consommateur de `SimState` : il ne décide rien, ne
 * mute rien, et peut être rebranché sur un état reçu du réseau (phase 3) sans
 * changement. Toutes les coordonnées du graphe de scène sont en TUILES ; le
 * conteneur `world` porte l'échelle px/tuile calculée par la caméra.
 *
 * Il ne connaît AUCUN jeu : les entités portent leur propre `appearance`
 * (forme + teintes) et les tuiles leur `tint` — aucun `if (type === 'block')`.
 *
 * Seule exception assumée : l'AMBIANCE. Chaque world a son thème de rendu
 * (`themes.ts`), choisi sur `level.meta.world`, qui décide du fond, du décor
 * d'arrière-plan et de quelques teintes de décor. Aucune règle de jeu n'en
 * dépend, et un world inconnu retombe sur le thème de l'Usine.
 */
export interface GameRenderer {
  /** Élément à insérer dans le DOM ; le canvas y est ajouté à l'init. */
  readonly element: HTMLElement;
  /** Résolue quand le canvas WebGL est prêt (attribut `data-renderer-ready`). */
  readonly ready: Promise<void>;
  /**
   * Redessine à partir de l'état courant (appelé à chaque frame).
   *
   * `dtMs` sert au FEEDBACK seul (particules, secousse) : le rendu observe la
   * simulation, il ne la pilote jamais.
   */
  draw(state: SimState | null, dtMs?: number): void;
  /** Champ de particules, exposé pour les tests et le diagnostic. */
  readonly particles: ParticleField;
  destroy(): void;
}

function drawLevel(target: Graphics, level: ParsedLevel, theme: WorldTheme): void {
  target.clear();

  // Fond du niveau (aplat ou ciel, selon le monde) + quadrillage, puis le
  // décor d'arrière-plan par-dessus la grille.
  theme.paintBackground(target, level);
  for (let tx = 1; tx < level.width; tx += 1) {
    target.rect(tx - 0.01, 0, 0.02, level.height).fill({ color: theme.gridLine });
  }
  for (let ty = 1; ty < level.height; ty += 1) {
    target.rect(0, ty - 0.01, level.width, 0.02).fill({ color: theme.gridLine });
  }
  theme.paintScenery(target, level);

  for (let ty = 0; ty < level.height; ty += 1) {
    for (let tx = 0; tx < level.width; tx += 1) {
      const tile = level.tileAt(tx, ty);
      if (tile.tint === null) {
        continue;
      }
      // Le thème peut redécorer une tuile ; les tuiles porteuses de sens
      // (`s c + - ! ~`) ne sont jamais dans `tileTints`.
      const tint = theme.tileTints[tile.char] ?? tile.tint;
      if (tile.marker) {
        // Porte de sortie : encadrement lumineux, laissé traversable.
        target.rect(tx + 0.12, ty + 0.05, 0.76, 0.95).fill({ color: tint, alpha: 0.22 });
        target.rect(tx + 0.12, ty + 0.05, 0.76, 0.95).stroke({ color: tint, width: 0.07 });
      } else if (tile.oneWay) {
        target.rect(tx, ty, 1, 0.18).fill({ color: tint });
      } else if (tile.lethal && !tile.solid) {
        for (let i = 0; i < 4; i += 1) {
          const base = tx + i * 0.25;
          target
            .moveTo(base, ty + 1)
            .lineTo(base + 0.125, ty + 0.35)
            .lineTo(base + 0.25, ty + 1)
            .fill({ color: tint });
        }
      } else if (!tile.solid) {
        // Zone traversable colorée (dégaussage) : hachures diagonales.
        target.rect(tx, ty, 1, 1).fill({ color: tint, alpha: theme.zoneAlpha });
        for (let i = 0; i < 3; i += 1) {
          const offset = i * 0.33;
          target
            .moveTo(tx + offset, ty + 1)
            .lineTo(tx + offset + 0.33, ty)
            .stroke({ color: tint, width: 0.05 });
        }
      } else {
        target.rect(tx, ty, 1, 1).fill({ color: tint });
        // Liseré supérieur : donne du relief aux murs sans coûter un sprite.
        target
          .rect(tx + 0.06, ty + 0.06, 0.88, 0.14)
          .fill({ color: theme.solidRim, alpha: theme.solidRimAlpha });
      }
    }
  }
}

function drawRobot(target: Graphics, tint: number, round: boolean): void {
  target.clear();
  const w = 0.8;
  const h = 0.9;
  if (round) {
    target.circle(w / 2, h / 2, w / 2).fill({ color: tint });
  } else {
    target.roundRect(0, 0, w, h, 0.18).fill({ color: tint });
  }
  // Visière
  target.rect(w * 0.2, h * 0.28, w * 0.6, h * 0.22).fill({ color: 0x0a0d14, alpha: 0.75 });
  // Antenne polaire
  target.rect(w / 2 - 0.03, -0.22, 0.06, 0.22).fill({ color: tint });
  target.circle(w / 2, -0.26, 0.09).fill({ color: tint });
}

function drawEntity(target: Graphics, entity: EntityInstance): void {
  if (entity.flags.collected === 1) {
    return;
  }
  const active = entity.flags.active === 1;
  const color = active ? entity.appearance.activeTint : entity.appearance.tint;
  const { x, y, w, h } = entity;

  switch (entity.appearance.shape) {
    case 'box':
      target.roundRect(x, y, w, h, 0.08).fill({ color });
      target.roundRect(x + 0.12, y + 0.12, w - 0.24, h - 0.24, 0.05).stroke({
        color: 0x0a0d14,
        width: 0.06,
        alpha: 0.6,
      });
      break;
    case 'plate':
      target.rect(x, active ? y + h * 0.4 : y, w, active ? h * 0.6 : h).fill({ color });
      break;
    case 'bar':
      target.rect(x, y, w, h).fill({ color, alpha: active ? 0.25 : 1 });
      break;
    case 'gem':
      target
        .moveTo(x + w / 2, y)
        .lineTo(x + w, y + h / 2)
        .lineTo(x + w / 2, y + h)
        .lineTo(x, y + h / 2)
        .fill({ color });
      break;
    case 'ring':
      target.circle(x + w / 2, y + h / 2, w / 2).stroke({ color, width: 0.16 });
      if (active) {
        target.circle(x + w / 2, y + h / 2, w * 0.8).stroke({ color, width: 0.05, alpha: 0.5 });
      }
      break;
    case 'lever':
      target.rect(x, y + h * 0.75, w, h * 0.25).fill({ color: 0x51607f });
      target
        .moveTo(x + w / 2, y + h * 0.8)
        .lineTo(x + (active ? w : 0), y)
        .stroke({ color, width: 0.14 });
      break;
  }
}

/** Ondes du champ magnétique + ligne de force entre les deux robots. */
function drawFields(target: Graphics, state: SimState): void {
  target.clear();
  const [polo, mag] = state.players;
  for (const player of state.players) {
    if (!player.fieldActive) {
      continue;
    }
    const cx = player.body.x + player.body.w / 2;
    const cy = player.body.y + player.body.h / 2;
    const color = player.character.tint;
    const phase = ((state.tick % 45) / 45) * MAGNET_RANGE_TILES;
    for (let ring = 0; ring < 3; ring += 1) {
      const radius = ((phase + ring * 2) % MAGNET_RANGE_TILES) + 0.5;
      target.circle(cx, cy, radius).stroke({
        color,
        width: 0.05,
        alpha: 0.45 * (1 - radius / (MAGNET_RANGE_TILES + 1)),
      });
    }
  }
  if (polo.flags.linked === 1 && mag.flags.linked === 1) {
    const repel = polo.flags.repelling === 1;
    target
      .moveTo(polo.body.x + polo.body.w / 2, polo.body.y + polo.body.h / 2)
      .lineTo(mag.body.x + mag.body.w / 2, mag.body.y + mag.body.h / 2)
      .stroke({ color: repel ? 0xffb347 : 0x9ff0ff, width: 0.1, alpha: 0.85 });
  }
}

export function createRenderer(): GameRenderer {
  const element = document.createElement('div');
  element.className = 'game-canvas';
  element.dataset.rendererReady = 'false';

  const app = new Application();
  const world = new Container();
  const levelLayer = new Graphics();
  const entityLayer = new Graphics();
  const fieldLayer = new Graphics();
  const particleLayer = new Graphics();
  const poloLayer = new Graphics();
  const magLayer = new Graphics();
  world.addChild(levelLayer, entityLayer, fieldLayer, particleLayer, poloLayer, magLayer);
  const particles = createParticleField();

  let destroyed = false;
  let initialized = false;
  let drawnLevel: ParsedLevel | null = null;
  let canvasColor = DEFAULT_THEME.canvas;
  let robotsDrawn = false;
  let viewWidth = 0;
  let viewHeight = 0;

  const ready = app
    .init({
      background: DEFAULT_THEME.canvas,
      antialias: true,
      autoStart: false,
      resizeTo: element,
      resolution: Math.min(globalThis.devicePixelRatio || 1, 2),
      autoDensity: true,
    })
    .then(() => {
      if (destroyed) {
        app.destroy(true);
        return;
      }
      app.stage.addChild(world);
      element.appendChild(app.canvas);
      initialized = true;
      element.dataset.rendererReady = 'true';
    });

  return {
    element,
    ready,
    particles,

    draw(state: SimState | null, dtMs = 0): void {
      if (!initialized || destroyed) {
        return;
      }
      particles.observe(state, dtMs);
      if (!state) {
        return;
      }
      // `resizeTo` de PixiJS s'appuie sur le ticker, désactivé ici
      // (`autoStart: false`) : c'est donc à la boucle de rendu de recaler la
      // taille du canvas quand l'écran change de disposition. Sans cela, la
      // caméra cadre un viewport périmé et le niveau se retrouve tassé dans un
      // coin.
      const width = element.clientWidth;
      const height = element.clientHeight;
      if (width > 0 && height > 0 && (width !== viewWidth || height !== viewHeight)) {
        viewWidth = width;
        viewHeight = height;
        app.renderer.resize(width, height);
      }
      const level = state.level;

      if (drawnLevel !== level) {
        const theme = themeForWorld(level.meta.world);
        drawLevel(levelLayer, level, theme);
        if (theme.canvas !== canvasColor) {
          canvasColor = theme.canvas;
          app.renderer.background.color = canvasColor;
        }
        drawnLevel = level;
        particles.reset();
      }
      if (!robotsDrawn) {
        drawRobot(poloLayer, state.players[0].character.tint, true);
        drawRobot(magLayer, state.players[1].character.tint, false);
        robotsDrawn = true;
      }

      entityLayer.clear();
      for (const entity of state.entities) {
        drawEntity(entityLayer, entity);
      }
      drawFields(fieldLayer, state);
      particles.draw(particleLayer);

      const [polo, mag] = state.players;
      poloLayer.position.set(polo.body.x, polo.body.y);
      magLayer.position.set(mag.body.x, mag.body.y);
      poloLayer.alpha = polo.dead ? 0.25 : 1;
      magLayer.alpha = mag.dead ? 0.25 : 1;
      // La polarité inversée se lit à la teinte du robot (concept §3.1).
      poloLayer.tint = polo.flags.inverted === 1 ? 0x8f7bff : 0xffffff;
      magLayer.tint = mag.flags.inverted === 1 ? 0xffd166 : 0xffffff;

      const camera = computeCamera({
        viewWidth,
        viewHeight,
        level,
        focusX: (polo.body.x + mag.body.x) / 2 + polo.body.w / 2,
        focusY: (polo.body.y + mag.body.y) / 2 + polo.body.h / 2,
      });
      // Secousse d'écran : déplacement en tuiles converti à l'échelle caméra.
      const shake = particles.shake();
      world.scale.set(camera.scale);
      world.position.set(camera.x + shake.x * camera.scale, camera.y + shake.y * camera.scale);

      app.renderer.render(app.stage);
    },

    destroy(): void {
      destroyed = true;
      particles.reset();
      if (initialized) {
        app.destroy(true, { children: true });
        initialized = false;
      }
      element.dataset.rendererReady = 'false';
    },
  };
}
